import React from 'react';
import { Building2, Users, Upload, Target, TrendingUp, AlertCircle } from 'lucide-react';
import Icon from '../../../components/AppIcon';


export default function SystemMetrics({ stats, companies }) {
  const metrics = [
    {
      title: 'Total Companies',
      value: stats?.total_companies || 0,
      icon: Building2,
      color: 'blue',
      description: 'Companies registered'
    },
    {
      title: 'Active Users',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'green',
      description: 'Active user accounts'
    },
    {
      title: 'File Uploads',
      value: stats?.total_uploads || 0,
      icon: Upload,
      color: 'purple',
      description: 'Files processed'
    },
    {
      title: 'Product Matches',
      value: stats?.total_matches || 0,
      icon: Target,
      color: 'orange',
      description: 'Matches generated'
    }
  ];

  const statusBreakdown = [
    {
      label: 'Active Companies',
      value: stats?.active_companies || 0,
      color: 'green'
    },
    {
      label: 'Trial Companies', 
      value: stats?.trial_companies || 0,
      color: 'yellow'
    },
    {
      label: 'Suspended',
      value: stats?.suspended_companies || 0,
      color: 'red'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-500 text-blue-600 bg-blue-50',
      green: 'bg-green-500 text-green-600 bg-green-50',
      purple: 'bg-purple-500 text-purple-600 bg-purple-50',
      orange: 'bg-orange-500 text-orange-600 bg-orange-50',
      yellow: 'bg-yellow-500 text-yellow-600 bg-yellow-50',
      red: 'bg-red-500 text-red-600 bg-red-50'
    };
    return colors?.[color] || colors?.blue;
  };

  return (
    <div className="space-y-6">
      {/* System Health Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">System Status: Operational</h2>
            <p className="text-blue-100">All systems running normally</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm">Live</span>
          </div>
        </div>
      </div>
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics?.map((metric, index) => {
          const Icon = metric?.icon;
          const colorClasses = getColorClasses(metric?.color)?.split(' ');
          
          return (
            <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${colorClasses?.[2]}`}>
                  <Icon className={`w-6 h-6 ${colorClasses?.[1]}`} />
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">{metric?.value?.toLocaleString()}</div>
                  <div className="text-sm text-gray-500">{metric?.title}</div>
                </div>
              </div>
              <p className="text-sm text-gray-600">{metric?.description}</p>
            </div>
          );
        })}
      </div>
      {/* Company Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Chart */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Company Status Overview</h3>
            <TrendingUp className="w-5 h-5 text-gray-400" />
          </div>
          
          <div className="space-y-4">
            {statusBreakdown?.map((item, index) => {
              const percentage = stats?.total_companies 
                ? Math.round((item?.value / stats?.total_companies) * 100)
                : 0;
              const colorClasses = getColorClasses(item?.color)?.split(' ');
              
              return (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">{item?.label}</span>
                    <span className="text-sm text-gray-500">{item?.value} ({percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${colorClasses?.[0]}`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Recent Companies</h3>
            <span className="text-sm text-gray-500">Last 5 registered</span>
          </div>

          <div className="space-y-3">
            {companies?.slice(0, 5)?.map((company, index) => (
              <div key={company?.id || index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-gray-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{company?.name}</div>
                    <div className="text-sm text-gray-500">{company?.slug}</div>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  company?.status === 'active' ? 'bg-green-100 text-green-800' :
                  company?.status === 'trial'? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                }`}>
                  {company?.status}
                </span>
              </div>
            )) || (
              <div className="text-center py-4 text-gray-500">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>No companies found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}